import React, {Fragment, useEffect, useRef, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {
    InfoIcon,
    InfoRow,
    InfoRowForUpload,
    LeftSection,
    SectionIcon,
    SectionTab,
    Wrapper,
    ContractsInfoItem,
    BalanceSection
} from "./AccountingRow.styles";
import CurrentAmountIcon from "../../../../public/images/current-amount-icon.svg";
import AddCreditIcon from "../../../../public/images/add-credit-icon.svg";
import {Alert, Form, Input, Modal, Spin, message} from "antd";
import {useStore} from "../../../../hooks/useStore";
import useIsFocused from "../../../../hooks/useIsFocused";
import {useFetch} from "../../../../hooks/useFetch";
import {useTranslation} from "react-i18next";
import {calculateKDV, calculateKDVAddedAmount, getKDVPercentage} from "../../../../utils/kdvHelper";
import {formatTurkishNumber, parseTurkishNumber} from "../../../../utils/moneyHelper";
import {DocumentModal} from "../../../documents/documentModal";
import {
    ContractDetails,
    InfoHeader,
    InfoItem
} from "components/layouts/customerLayout/components/customerAccountingDetailInfo/CustomerAccountingDetailInfo.styles";
import {CaretRightOutlined} from "@ant-design/icons";
import ContratCalculator from "../../../../public/images/contract-calculator.svg";
import {CustomButton} from "components/_defaultComponents/CustomButton/CustomButton";
import {CustomerCodAggrementBlock} from "components/layouts/customerLayout/components/customerCodAggrementBlock/CustomerCodAggrementBlock";
import {MissingDocumentsModal} from "components/_defaultComponents/MissingDocumentsModal/MissingDocumentsModal";
import moment from "moment";
import {stripEmpty} from "../../../../utils/stripEmpty";
import {formatNumberString} from "../../../../utils/helpers";
import UploadCreditModal from "../components/UploadCreditModal";
import {observer} from "mobx-react-lite";
const RELOAD_INTERVAL = 1000 * 10; //10 seconds

const getDefaultTimeRange = () => {
    const now = moment();
    const currentHour = now.hour();
    const stores = useStore();

    // Case 1: Between 00:00-06:00
    if (currentHour >= 0 && currentHour < 6) {
        const startDate = moment()
            .subtract(1, "day")
            .set({hour: 6, minute: 0, second: 0, millisecond: 0});
        const endDate = moment().set({hour: 6, minute: 0, second: 0, millisecond: 0});
        return [startDate, endDate];
    }

    // Case 2: Between 06:00-23:59
    if (currentHour >= 6 && currentHour <= 23) {
        const startDate = moment().set({hour: 6, minute: 0, second: 0, millisecond: 0});
        const endDate = moment();
        return [startDate, endDate];
    }

    // Case 3: Default case (should not normally occur)
    const startDate = moment().set({hour: 6, minute: 0, second: 0, millisecond: 0});
    const endDate = moment()
        .add(1, "day")
        .set({hour: 6, minute: 0, second: 0, millisecond: 0});
    return [startDate, endDate];
};

export const AccountingRow = observer(() => {
    const history = useHistory();
    const location = useLocation();
    const stores = useStore();
    const isFocused = useIsFocused();
    const {t} = useTranslation();
    const noWidthForRightSection =
        location.pathname === "/musteri" || location.pathname === "/musteri/hesabim";
    const [recall, setRecall] = useState(true);
    const [isCreditModalActive, setIsCreditModalActive] = useState(false);
    const [newCreditAmount, setNewCreditAmount] = useState(0);
    const [displayValue, setDisplayValue] = useState("");
    const recallRef = useRef(recall);
    const [documentModalVisible, setDocumentModalVisible] = useState(false);
    const [documentUrl, setDocumentUrl] = useState(null);
    const [isNeedForApproval, setIsNeedForApproval] = useState(null);
    const [isApproved, setIsApproved] = useState(null);
    const [contractId, setContractId] = useState(null);
    const [contracts, setContracts] = useState([]);
    const [summary, setSummary] = useState(null);
    const isCodPage = location.pathname === "/musteri/kapida-odeme";
    recallRef.current = recall;
    const subCompanyId =
        stores?.meStore?.selectedSubCompanyId || stores?.meStore?.subcompanies?.[0]?._id;
    const isAccountingPage = location.pathname === "/musteri/muhasebe";
    const [filters, setFilters] = useState({
        startDate: getDefaultTimeRange()[0],
        endDate: getDefaultTimeRange()[1]
    });
    const [isPaymentLinkLoading, setIsPaymentLinkLoading] = useState(false);
    const [isLoadingBalance, setIsLoadingBalance] = useState(false);
    const [missingDocumentsModalVisible, setMissingDocumentsModalVisible] = useState(false);

    const [creditDataLoading, creditData, _, __] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.subCompanyStore.getCredit.bind(null, {subCompanyId}),
        dependencies: [recall, subCompanyId],
        initialCall: true
    });

    const subcompanyCredit = creditData?.credit;

    const [subCompanyPaymentContractsLoading, subCompanyPaymentContracts, refetch] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyPaymentContracts.bind(null, {
            subCompanyId
        }),
        dependencies: [subCompanyId]
    });

    useEffect(() => {
        const fetchPaymentSummary = async () => {
            if (!subCompanyId) return;

            try {
                const [startDate, endDate] = getDefaultTimeRange();
                const filters = {
                    startDate,
                    endDate
                };

                const raw = {
                    ...filters,
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD"),
                    startTime: startDate.format("HH:mm"),
                    endTime: endDate.format("HH:mm")
                };

                const payload = stripEmpty(raw);
                const summaryRes = await stores.codPaymentStore.getPaymentSummary(payload);
                setSummary(summaryRes || {});
            } catch (error) {
                console.error("Error fetching payment summary:", error);
                setSummary({});
            }
        };

        fetchPaymentSummary();
    }, [filters, subCompanyId, stores.codPaymentStore]);

    useEffect(() => {
        const activeContractPrices =
            subCompanyPaymentContracts?.activeContract?.feeItems.filter(
                item => !item?.options?.isCustomTimed
            ) || [];
        setContracts(activeContractPrices);
    }, [subCompanyPaymentContracts]);

    useEffect(() => {
        setTimeout(() => {
            setRecall(!recallRef.current);
        }, 200);
    }, []);

    useEffect(() => {
        if (isFocused) {
            let timeout = null;

            timeout = setTimeout(() => {
                setRecall(!recallRef.current);
            }, RELOAD_INTERVAL);

            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    const createPaymentLink = async () => {
        setIsPaymentLinkLoading(true);

        try {
            const response = await stores.companyStore.createPaymentLink({
                subCompanyId: subCompanyId,
                amount: newCreditAmount
            });

            if (response?.paymentUrl) {
                window.open(response?.paymentUrl, "_blank", "norefferer");
            } else if (Boolean(response)) {
                message.error("Ödeme linki oluşturulurken bir hata oluştu.");
            }
        } catch (error) {
            message.error("Ödeme linki oluşturulurken bir hata oluştu.");
        } finally {
            setIsPaymentLinkLoading(false);
        }
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const status = urlParams.get("status");

        if (status === "success") {
            message.success("Ödemeniz başarıyla gerçekleşti. Bakiyeniz güncellendi.");
        } else if (status === "fail") {
            message.error("Ödemeniz gerçekleştirilemedi. Lütfen tekrar deneyin.");
        }
    }, []);
    // todo: same logic appears in CustomerAccountingRow, change there when you change this.
    const checkUnapprovedDocuments = async ({subCompanyId}) => {
        const contractName = "serviceAgreement";
        const params = {subCompanyId, contractName: contractName};
        const contract = await stores.contractStore.createAndShowContract(params);
        if (contract?.data?.success === false) {
            const missingFields = contract?.data?.error?.missingFields?.tr || [];
            setMissingDocumentsModalVisible(true);
            return "missing_documents";
        }
        if (
            contract?.isExistingContract === undefined ||
            (contract?.isExistingContract === true &&
                contract?.isNeedForApproval &&
                !contract?.isApproved)
        ) {
            setDocumentUrl(contract.contractPath);
            setIsNeedForApproval(true);
            setIsApproved(false);
            setDocumentModalVisible(true);

            await stores.contractStore.changeApprovalStatus({
                contractId: contract.contractId,
                isShown: true
            });
            setContractId(contract.contractId);
            return true;
        }
        return false;
    };

    const handleUpdateCredit = async () => {
        setIsLoadingBalance(true);

        const result = await checkUnapprovedDocuments({subCompanyId: subCompanyId});
        if (result === false) {
            setIsCreditModalActive(true);
        }

        setIsLoadingBalance(false);
    };

    const handleOnCancelDocumentModal = async () => {
        if (documentModalVisible && documentUrl) {
            const payload = {
                contractId: contractId,
                isApproved: false
            };
            await stores.contractStore.changeApprovalStatus(payload);
        }
        setDocumentModalVisible(false);
        setDocumentUrl(null);
        setContractId(null);
        setRecall(!recallRef.current);
    };

    const handleOnApprove = async () => {
        const payload = {
            contractId: contractId,
            isApproved: true
        };
        await stores.contractStore.changeApprovalStatus(payload);
        setDocumentModalVisible(false);
        setIsCreditModalActive(true);
        setDocumentUrl(null);
        setContractId(null);
    };

    const handleInputChange = e => {
        const value = e.target.value;

        if (!/^[\d.,]*$/.test(value)) {
            return;
        }

        if (
            value.startsWith("0") &&
            value.length > 1 &&
            !value.includes(",") &&
            !value.includes(".")
        ) {
            return;
        }

        const formattedValue = formatTurkishNumber(value);
        const numericValue = parseTurkishNumber(formattedValue);

        setDisplayValue(formattedValue);
        setNewCreditAmount(numericValue);
    };

    useEffect(() => {
        if (isCreditModalActive) {
            setDisplayValue("");
            setNewCreditAmount(0);
        }
    }, [isCreditModalActive]);

    const shouldShowCodBlock = summary?.codCommissionRate != null;

    return (
        <Fragment>
            {isLoadingBalance && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(255, 255, 255, 0.7)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999
                    }}
                >
                    <Spin size="large" />
                </div>
            )}
            <Wrapper $noWidth={noWidthForRightSection}>
                {/* <SectionTab>
                    <SectionIcon src={AccountingSectionTabIcon} />
                    Muhasebe
                </SectionTab> */}
                <LeftSection>
                    <InfoItem>
                        <BalanceSection>
                            <div
                                className="balance-info"
                                style={{
                                    opacity: creditDataLoading ? 0.6 : 1
                                }}
                                onClick={() => {
                                    if (!creditDataLoading) {
                                        history.push("/musteri/muhasebe");
                                    }
                                }}
                            >
                                <InfoHeader>
                                    <img src={CurrentAmountIcon} alt="current amount icon" />
                                    <b>Bakiyem:</b>
                                </InfoHeader>
                                <div className="balance-value">
                                    {creditDataLoading ? (
                                        <Spin size="small" />
                                    ) : subcompanyCredit === 9999999 ? (
                                        <span style={{fontSize: "14px"}}>Kredi ile çalışmıyor</span>
                                    ) : (
                                        `${
                                            subcompanyCredit && subcompanyCredit !== 9999999
                                                ? formatNumberString(subcompanyCredit)
                                                : 0
                                        } ₺`
                                    )}
                                </div>
                            </div>
                            {subcompanyCredit !== 9999999 && (
                                <div
                                    className="balance-load-btn"
                                    style={{
                                        opacity: creditDataLoading ? 0.6 : 1,
                                        cursor: creditDataLoading ? "not-allowed" : "pointer"
                                    }}
                                    onClick={() => !creditDataLoading && handleUpdateCredit()}
                                >
                                    {creditDataLoading ? (
                                        <Spin size="small" />
                                    ) : (
                                        <img
                                            src={AddCreditIcon}
                                            alt="add credit icon"
                                            style={{width: "16px", height: "16px"}}
                                        />
                                    )}
                                    Bakiye Yükle
                                </div>
                            )}
                        </BalanceSection>
                    </InfoItem>
                </LeftSection>
                {isAccountingPage && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                            width: "100%"
                        }}
                    >
                        <InfoItem>
                            <InfoHeader>
                                <img src={ContratCalculator} alt="contract calculator icon" />
                                <b>Güncel Sözleşme</b>
                            </InfoHeader>
                        </InfoItem>
                        <InfoItem>
                            <ContractsInfoItem>
                                <div className="contracts-list">
                                    {contracts?.map(contract => (
                                        <div key={contract?._id} className="contract-row">
                                            <b>
                                                {contract?.feeType === "PAKET" && "Paket Ücreti:"}
                                                {contract?.feeType === "KM" && "Mesafe Ücreti:"}
                                            </b>
                                            <b className="contract-amount">
                                                {contract?.feeType === "PAKET" &&
                                                    `${contract?.clientAmount}₺`}
                                                {contract?.feeType === "KM" &&
                                                    `${contract?.clientAmount}₺`}
                                            </b>
                                        </div>
                                    ))}
                                </div>
                                <ContractDetails>
                                    <CustomButton
                                        href={"/musteri/sozlesme"}
                                        className="all-contracts-btn"
                                    >
                                        Tüm Sözleşmeler
                                        <CaretRightOutlined />
                                    </CustomButton>
                                </ContractDetails>
                            </ContractsInfoItem>
                        </InfoItem>
                    </div>
                )}
                {isCodPage && (
                    <CustomerCodAggrementBlock
                        isBackoffice={false}
                        codCommissionRate={summary?.codCommissionRate}
                        externalRecall={recall}
                    />
                )}
            </Wrapper>
            <DocumentModal
                visibility={documentModalVisible && documentUrl !== null}
                onCancel={handleOnCancelDocumentModal}
                documentUrl={documentUrl}
                title="Onay Bekleyen Sözleşme"
                getContainer={false}
                onApprove={handleOnApprove}
                isNeedForApproval={isNeedForApproval}
                isApproved={isApproved}
                contractId={contractId}
                isApproveButtonDisabled={false}
            />
            <MissingDocumentsModal
                visible={missingDocumentsModalVisible}
                onCancel={() => setMissingDocumentsModalVisible(false)}
                onOk={() => setMissingDocumentsModalVisible(false)}
            />
            <UploadCreditModal
                isCreditModalActive={isCreditModalActive}
                setIsCreditModalActive={setIsCreditModalActive}
                newCreditAmount={newCreditAmount}
                setNewCreditAmount={setNewCreditAmount}
                displayValue={displayValue}
                handleInputChange={handleInputChange}
                createPaymentLink={createPaymentLink}
                isPaymentLinkLoading={isPaymentLinkLoading}
                getKDVPercentage={getKDVPercentage}
                calculateKDV={calculateKDV}
                calculateKDVAddedAmount={calculateKDVAddedAmount}
            />
        </Fragment>
    );
});
